import type { MouseEvent } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import LinkButton from '_components/UI/LinkButton';

import type { ActivityNavigationItem } from '../types';

import styles from './ActivityCard.module.scss';

export interface ActivityCardProps extends ActivityNavigationItem {
  /** Skips lazy loading for the cards that are on screen on arrival. */
  priority?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * A single image-based navigation card.
 *
 * The whole card is one `<a>`, so there is exactly one tab stop and the focus
 * ring wraps the entire tile. The CTA reuses `LinkButton` with `as="span"`
 * because a nested `<a>` would be invalid markup and would add a second tab
 * stop for something that goes to the same place as the card itself.
 */
const ActivityCard = ({
  label,
  href,
  image,
  ctaLabel,
  priority = false,
  onClick,
}: ActivityCardProps) => {
  const { t } = useTranslation('common');

  return (
    <a href={href} className={styles.card} onClick={onClick}>
      <span className={styles.imageWrapper}>
        <Image
          src={image.url}
          alt={image.alt}
          fill
          // The card is a fixed 330px at every breakpoint, so a single
          // candidate width is all the browser needs.
          sizes="330px"
          priority={priority}
          className={styles.image}
        />
      </span>
      {/* Gradient scrim keeps the white label above the WCAG AA ratio no
          matter how light the photo behind it is. */}
      <span className={styles.scrim} aria-hidden="true" />
      <span className={styles.content}>
        <span className={styles.label}>{label}</span>
        {/* Hidden from assistive tech: it goes exactly where the card
            already goes, so announcing it only lengthens the link's name. */}
        <span className={styles.ctaWrapper} aria-hidden="true">
          <LinkButton as="span" variant="white" className={styles.cta}>
            {ctaLabel || t('activityNavigationGrid.cta')}
          </LinkButton>
        </span>
      </span>
    </a>
  );
};

export default ActivityCard;
